import React, { useState } from "react";
import { Drawer, Box, Typography, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PublicIcon from "@mui/icons-material/Public";
import IPDetailsView from "./IPDetailsView";
import BrowseHistoryView from "./BrowseHistoryView";
import IPGeoView from "./IPGeoView";

const TAB_DETAILS = "details";
const TAB_GEO = "geo";

const TrafficDetailDrawer = ({ open, onClose, trafficData, onViewAlerts, onEmailClick, onUpdate }) => {
    const [view, setView] = useState(TAB_DETAILS); // 'details', 'history', 'geo'
    const [activeTab, setActiveTab] = useState(TAB_DETAILS); // controls top tabs

    const handleBack = () => {
        setView(activeTab); // back always returns to whichever top-tab is active
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setView(tab);
    };

    const isHistoryView = view === "history";

    const renderContent = () => {
        switch (view) {
            case "history":
                return <BrowseHistoryView onBack={handleBack} trafficData={trafficData} />;
            case TAB_GEO:
                return <IPGeoView ip={trafficData?.ip} />;
            default:
                return (
                    <IPDetailsView
                        trafficData={trafficData}
                        onViewHistory={() => setView("history")}
                        onViewAlerts={onViewAlerts}
                        onEmailClick={onEmailClick}
                        onUpdate={onUpdate}
                    />
                );
        }
    };

    const getTitle = () => {
        if (view === "history") return "Page Views";
        return trafficData?.ip || "—";
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            sx={{
                "& .MuiDrawer-paper": {
                    width: "450px",
                    top: "0px !important",
                    height: "100%",
                    p: 0,
                    backgroundColor: "#fafafa"
                }
            }}
        >
            <Box sx={{ p: 0, display: "flex", flexDirection: "column", height: "100%" }}>
                {/* Header */}
                <Box
                    className="d-flex justify-content-between align-items-center px-3 py-3 bg-white"
                    style={{ borderBottom: "1px solid #EEEEEE", flexShrink: 0 }}
                >
                    <Box className="d-flex align-items-center gap-2">
                        {isHistoryView && (
                            <IconButton onClick={handleBack} size="small" sx={{ mr: 1, backgroundColor: "#F5F5F5" }}>
                                <ArrowBackIcon sx={{ fontSize: "18px" }} />
                            </IconButton>
                        )}
                        <Typography className="sofiaBold" style={{ fontSize: "18px", color: "#1D1D1B" }}>
                            {getTitle()}
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose} size="small" sx={{ backgroundColor: "#F5F5F5" }}>
                        <CloseIcon sx={{ fontSize: "18px" }} />
                    </IconButton>
                </Box>

                {/* Tab strip — only show when not in history sub-view */}
                {!isHistoryView && (
                    <Box
                        sx={{
                            display: "flex",
                            backgroundColor: "#fff",
                            borderBottom: "1px solid #EEEEEE",
                            flexShrink: 0,
                            px: 2,
                            gap: "4px",
                        }}
                    >
                        {[
                            { id: TAB_DETAILS, label: "Details" },
                            { id: TAB_GEO, label: "IP Info", icon: <PublicIcon sx={{ fontSize: "14px", mr: "4px" }} /> },
                        ].map((tab) => (
                            <Box
                                key={tab.id}
                                onClick={() => handleTabChange(tab.id)}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    px: 2,
                                    py: "10px",
                                    cursor: "pointer",
                                    fontFamily: "Sofia Pro Bold, sans-serif",
                                    fontSize: "13px",
                                    color: activeTab === tab.id ? "#1D1D1B" : "#BBBBBB",
                                    borderBottom: activeTab === tab.id ? "2px solid #1D1D1B" : "2px solid transparent",
                                    transition: "all 0.15s ease",
                                    userSelect: "none",
                                    "&:hover": {
                                        color: "#1D1D1B",
                                    },
                                }}
                            >
                                {tab.icon || null}
                                {tab.label}
                            </Box>
                        ))}
                    </Box>
                )}

                {/* Scrollable content */}
                <Box sx={{ flex: 1, overflowY: "auto" }}>
                    {renderContent()}
                </Box>
            </Box>
        </Drawer>
    );
};

export default TrafficDetailDrawer;
